import { CarAttentionItemDto, CarAttentionKindDto, CarAttentionSeverityDto } from '@/api/generated';
import { themeColors } from '@/config/theme/themeColors.ts';
import { PrivalleyTFunction } from '@/utils/useTranslationWithPrefix.ts';

const ISSUE_DESCRIPTION_LIMIT = 48;

export const isUrgent = (item: CarAttentionItemDto): boolean =>
  item.severity !== CarAttentionSeverityDto.Info;

export const severityColors = (
  severity: CarAttentionSeverityDto,
): { background: string; color: string } => {
  switch (severity) {
    case CarAttentionSeverityDto.Critical:
      return { background: themeColors.red, color: themeColors.white };
    case CarAttentionSeverityDto.Info:
      return { background: themeColors.stroke, color: themeColors.greyDarker };
    case CarAttentionSeverityDto.Warning:
      return { background: themeColors.orange, color: themeColors.white };
  }
};

export const relativeDays = (days: number, t: PrivalleyTFunction): string => {
  if (days === 0) {
    return t('relative.today');
  }
  if (days === 1) {
    return t('relative.tomorrow');
  }
  if (days === -1) {
    return t('relative.yesterday');
  }
  return days > 0
    ? t('relative.inDays', { days })
    : t('relative.daysAgo', { days: Math.abs(days) });
};

export const formatKilometres = (km: number): string =>
  new Intl.NumberFormat('pl-PL', { useGrouping: 'always' }).format(km);

const shorten = (text: string | undefined): string => {
  if (!text) {
    return '';
  }
  return text.length > ISSUE_DESCRIPTION_LIMIT
    ? `${text.slice(0, ISSUE_DESCRIPTION_LIMIT - 1).trimEnd()}…`
    : text;
};

export const carAttentionLabel = (item: CarAttentionItemDto, t: PrivalleyTFunction): string => {
  const type = item.deadlineType?.name ?? '';
  switch (item.kind) {
    case CarAttentionKindDto.DeadlineDueSoon:
      if (item.remainingDays !== undefined && item.remainingDays <= 30) {
        return t('attention.labels.soonDate', { type, when: relativeDays(item.remainingDays, t) });
      }
      return t('attention.labels.soonMileage', {
        km: formatKilometres(item.remainingMileage ?? 0),
        type,
      });
    case CarAttentionKindDto.DeadlineMissing:
      return t('attention.labels.missing', { type });
    case CarAttentionKindDto.DeadlineOverdue:
      if (item.remainingDays !== undefined && item.remainingDays < 0) {
        return t('attention.labels.overdueDate', {
          type,
          when: relativeDays(item.remainingDays, t),
        });
      }
      return t('attention.labels.overdueMileage', {
        km: formatKilometres(Math.abs(item.remainingMileage ?? 0)),
        type,
      });
    case CarAttentionKindDto.DeadlinePlannedDatePassed:
      return t('attention.labels.plannedDatePassed', { date: item.plannedDate ?? '', type });
    case CarAttentionKindDto.IssueGrounded:
      return t('attention.labels.grounded', { description: shorten(item.issueDescription) });
    case CarAttentionKindDto.IssueOpen:
      return t('attention.labels.openIssue', { description: shorten(item.issueDescription) });
  }
};

export type CarAttentionGroup = {
  carId: number;
  items: CarAttentionItemDto[];
  licensePlate: string;
  model: string;
  responsibleUserId?: string;
  severity: CarAttentionSeverityDto;
};

const SEVERITY_ORDER: CarAttentionSeverityDto[] = [
  CarAttentionSeverityDto.Critical,
  CarAttentionSeverityDto.Warning,
  CarAttentionSeverityDto.Info,
];

export const groupByCar = (items: CarAttentionItemDto[]): CarAttentionGroup[] => {
  const groups = new Map<number, CarAttentionGroup>();
  items.forEach(item => {
    const group = groups.get(item.carId);
    if (group) {
      group.items.push(item);
      return;
    }
    groups.set(item.carId, {
      carId: item.carId,
      items: [item],
      licensePlate: item.licensePlate,
      model: item.model,
      responsibleUserId: item.responsibleUserId,
      severity: item.severity,
    });
  });
  return [...groups.values()];
};

export const mostSevere = (items: CarAttentionItemDto[]): CarAttentionSeverityDto | undefined =>
  SEVERITY_ORDER.find(severity => items.some(item => item.severity === severity));
